import { z } from "zod";
import { IdPageQuery, IsoDateTime, Uuid, pageOf } from "./common";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const AD_PLATFORMS = ["meta", "google"] as const;
export const AdPlatform = z.enum(AD_PLATFORMS);
export type AdPlatform = z.infer<typeof AdPlatform>;

export const AD_CONNECTION_STATUSES = ["pending", "needs_account", "active", "expired", "error", "disconnected"] as const;
export const AdConnectionStatus = z.enum(AD_CONNECTION_STATUSES);
export type AdConnectionStatus = z.infer<typeof AdConnectionStatus>;

export const AD_AUTH_KINDS = ["oauth", "system_user"] as const;

/** What was set on the entity. `unknown` is a value the platform added later: treated as able to spend. */
export const AD_ENTITY_STATUSES = ["active", "paused", "removed", "unknown"] as const;
export const AdEntityStatus = z.enum(AD_ENTITY_STATUSES);
export type AdEntityStatus = z.infer<typeof AdEntityStatus>;

export const AD_LEVELS = ["campaign", "ad_set", "ad", "keyword"] as const;
export const AdLevel = z.enum(AD_LEVELS);
export type AdLevel = z.infer<typeof AdLevel>;

export const AD_CAMPAIGN_ROLES = ["test", "scale", "retargeting"] as const;
export const AdCampaignRole = z.enum(AD_CAMPAIGN_ROLES);
export type AdCampaignRole = z.infer<typeof AdCampaignRole>;

export const AD_ACTION_KINDS = ["pause", "start", "budget", "cost_cap", "role", "emergency_stop", "emergency_release", "create"] as const;
export const AdActionKind = z.enum(AD_ACTION_KINDS);
export type AdActionKind = z.infer<typeof AdActionKind>;

export const AD_ACTION_SOURCES = ["user", "guard", "emergency_stop", "engine"] as const;
export const AdActionSource = z.enum(AD_ACTION_SOURCES);
export type AdActionSource = z.infer<typeof AdActionSource>;

/** pending: written, platform not answered yet. unconfirmed: the platform may have applied it; a later sync settles it. */
export const AD_ACTION_STATUSES = ["pending", "succeeded", "failed", "unconfirmed", "dry_run", "refused"] as const;
export const AdActionStatus = z.enum(AD_ACTION_STATUSES);
export type AdActionStatus = z.infer<typeof AdActionStatus>;

export const AD_ALERT_CODES = ["daily_cap_crossed", "monthly_cap_crossed", "spend_unknown", "auto_pause_failed", "connection_auth", "disconnected_active", "promotion_stop_failed"] as const;
export const AdAlertCode = z.enum(AD_ALERT_CODES);
export type AdAlertCode = z.infer<typeof AdAlertCode>;

export const AD_ALERT_SEVERITIES = ["info", "warning", "critical"] as const;
export const AdAlertSeverity = z.enum(AD_ALERT_SEVERITIES);
export type AdAlertSeverity = z.infer<typeof AdAlertSeverity>;

/** Why a connection's spend cannot be trusted. Never read as zero. */
export const SPEND_UNKNOWN_REASONS = ["never_synced", "sync_failed", "sync_stale", "currency_mismatch"] as const;
export const SpendUnknownReason = z.enum(SPEND_UNKNOWN_REASONS);
export type SpendUnknownReason = z.infer<typeof SpendUnknownReason>;

/** Stable codes of a refused action, for the UI to translate. */
export const AD_REFUSAL_CODES = [
  "emergency_stop_active",
  "spend_unknown",
  "previous_change_unconfirmed",
  "guard_caps_required",
  "daily_cap_crossed",
  "monthly_cap_crossed",
  "budget_over_daily_cap",
  "budget_over_monthly_cap",
  "budget_growth_exceeded",
  "campaign_daily_budget_unknown",
] as const;
export const AdRefusalCode = z.enum(AD_REFUSAL_CODES);
export type AdRefusalCode = z.infer<typeof AdRefusalCode>;

const Currency = z.string().regex(/^[A-Za-z]{3}$/).transform((value) => value.toUpperCase());
const MinorAmount = z.number().int().positive().max(1_000_000_000_000);
const IsoDay = z.iso.date();
/** `z.coerce.boolean` reads the query text "false" as true. */
const QueryFlag = z.union([z.boolean(), z.enum(["true", "false"]).transform((value) => value === "true")]);

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

/** No token, secret or nonce here: an account owner's token is never shown back. */
export const AdConnectionDto = z.object({
  id: Uuid,
  platform: AdPlatform,
  status: AdConnectionStatus,
  name: z.string(),
  authKind: z.enum(AD_AUTH_KINDS),
  connected: z.boolean(),
  externalAccountId: z.string().nullable(),
  accountName: z.string().nullable(),
  currency: z.string().nullable(),
  timeZone: z.string().nullable(),
  scopes: z.array(z.string()),
  tokenExpiresAt: IsoDateTime.nullable(),
  connectedAt: IsoDateTime.nullable(),
  lastSyncAt: IsoDateTime.nullable(),
  lastSyncAttemptAt: IsoDateTime.nullable(),
  lastSyncResult: z.unknown().nullable(),
  spendSyncedAt: IsoDateTime.nullable(),
  spendSyncError: z.string().nullable(),
  lastError: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type AdConnectionDto = z.infer<typeof AdConnectionDto>;
export const AdConnectionPage = pageOf(AdConnectionDto);
export const AdConnectionListQuery = IdPageQuery.extend({ platform: AdPlatform.optional() });

export const AdConnectionCreate = z.object({ platform: AdPlatform, name: z.string().trim().min(1).max(200) });
export const AdOAuthStartDto = z.object({ authorizeUrl: z.string().url() });

export const AdAccountDto = z.object({
  externalId: z.string(),
  name: z.string(),
  currency: z.string().nullable(),
  timeZone: z.string().nullable(),
  /** False when the platform says the account cannot run ads right now. */
  usable: z.boolean(),
});
export type AdAccountDto = z.infer<typeof AdAccountDto>;
export const AdAccountList = z.object({ items: z.array(AdAccountDto) });
export const AdSelectAccountInput = z.object({ externalId: z.string().trim().min(1).max(100) });

/** The Meta route without OAuth: a system user token made in the tenant's Business Manager. */
export const AdSystemUserTokenInput = z.object({
  token: z.string().trim().min(20).max(2000),
  /** Needed when the token opens more than one account. */
  externalAccountId: z.string().trim().min(1).max(100).optional(),
});

export const AdConnectionTestDto = z.object({
  ok: z.boolean(),
  errorCode: z.string().nullable(),
  account: AdAccountDto.nullable(),
  checkedAt: IsoDateTime,
});
export type AdConnectionTestDto = z.infer<typeof AdConnectionTestDto>;

// ---------------------------------------------------------------------------
// Hierarchy and metrics
// ---------------------------------------------------------------------------

const EntityBase = {
  id: Uuid,
  connectionId: Uuid,
  externalId: z.string(),
  name: z.string(),
  status: AdEntityStatus,
  effectiveStatus: z.string().nullable(),
  removedAt: IsoDateTime.nullable(),
  lastSeenAt: IsoDateTime,
};

export const AdCampaignDto = z.object({
  ...EntityBase,
  objective: z.string().nullable(),
  channelType: z.string().nullable(),
  currency: z.string(),
  dailyBudgetMinor: z.number().int().nullable(),
  lifetimeBudgetMinor: z.number().int().nullable(),
  budgetShared: z.boolean(),
  bidStrategy: z.string().nullable(),
  portfolioStrategy: z.boolean(),
  costCapMinor: z.number().int().nullable(),
  role: AdCampaignRole.nullable(),
  startAt: IsoDateTime.nullable(),
  stopAt: IsoDateTime.nullable(),
  autoPausedAt: IsoDateTime.nullable(),
  autoPauseReason: z.string().nullable(),
  /** This campaign's own target; none means the tenant's applies. It reports and stops nothing. */
  targetCurrency: z.string().nullable(),
  targetCostPerLeadMinor: z.number().int().nullable(),
  targetCostPerClientMinor: z.number().int().nullable(),
});
export type AdCampaignDto = z.infer<typeof AdCampaignDto>;
export const AdCampaignPage = pageOf(AdCampaignDto);
export const AdCampaignTargetsInput = z.strictObject({
  costPerLeadMinor: MinorAmount.nullable(),
  costPerClientMinor: MinorAmount.nullable(),
});
export type AdCampaignTargetsInput = z.infer<typeof AdCampaignTargetsInput>;
export const AdCampaignListQuery = IdPageQuery.extend({ connectionId: Uuid.optional(), status: AdEntityStatus.optional(), includeRemoved: QueryFlag.optional() });

export const AdSetDto = z.object({
  ...EntityBase,
  campaignId: Uuid,
  dailyBudgetMinor: z.number().int().nullable(),
  lifetimeBudgetMinor: z.number().int().nullable(),
  bidStrategy: z.string().nullable(),
  costCapMinor: z.number().int().nullable(),
  optimizationGoal: z.string().nullable(),
});
export type AdSetDto = z.infer<typeof AdSetDto>;
export const AdSetPage = pageOf(AdSetDto);
export const AdSetListQuery = IdPageQuery.extend({ campaignId: Uuid.optional(), includeRemoved: QueryFlag.optional() });

export const AdDto = z.object({ ...EntityBase, campaignId: Uuid, adSetId: Uuid, adType: z.string().nullable(), creative: z.unknown().nullable() });
export type AdDto = z.infer<typeof AdDto>;
export const AdPage = pageOf(AdDto);
export const AdListQuery = IdPageQuery.extend({ adSetId: Uuid.optional(), includeRemoved: QueryFlag.optional() });

export const AdKeywordDto = z.object({
  id: Uuid,
  connectionId: Uuid,
  campaignId: Uuid,
  adSetId: Uuid,
  externalId: z.string(),
  text: z.string(),
  matchType: z.string().nullable(),
  negative: z.boolean(),
  status: AdEntityStatus,
  removedAt: IsoDateTime.nullable(),
  lastSeenAt: IsoDateTime,
});
export type AdKeywordDto = z.infer<typeof AdKeywordDto>;
export const AdKeywordPage = pageOf(AdKeywordDto);
export const AdKeywordListQuery = IdPageQuery.extend({ adSetId: Uuid.optional(), includeRemoved: QueryFlag.optional() });

export const AdMetricsQuery = z.object({ level: AdLevel.default("campaign"), from: IsoDay, to: IsoDay, connectionId: Uuid.optional() });

/** One entity's numbers over the range, in the account's currency. */
export const AdMetricTotalDto = z.object({
  connectionId: Uuid,
  level: AdLevel,
  externalId: z.string(),
  currency: z.string(),
  spendMinor: z.number().int(),
  impressions: z.number().int(),
  clicks: z.number().int(),
  leads: z.number().int(),
  conversions: z.number(),
});
export type AdMetricTotalDto = z.infer<typeof AdMetricTotalDto>;
export const AdMetricTotals = z.object({ items: z.array(AdMetricTotalDto) });

export const AdDailyMetricsQuery = z.object({ connectionId: Uuid, level: AdLevel.default("campaign"), externalId: z.string().min(1).max(200), from: IsoDay, to: IsoDay });
export const AdDailyMetricDto = z.object({
  date: IsoDay,
  currency: z.string(),
  spendMinor: z.number().int(),
  impressions: z.number().int(),
  clicks: z.number().int(),
  reach: z.number().int().nullable(),
  leads: z.number().int(),
  conversions: z.number(),
});
export type AdDailyMetricDto = z.infer<typeof AdDailyMetricDto>;
export const AdDailyMetrics = z.object({ items: z.array(AdDailyMetricDto) });

export const AdSyncReportDto = z.object({
  connectionId: Uuid,
  skipped: z.boolean(),
  ok: z.boolean(),
  spendKnown: z.boolean(),
  days: z.number().int(),
  campaigns: z.number().int(),
  adSets: z.number().int(),
  ads: z.number().int(),
  keywords: z.number().int(),
  metrics: z.number().int(),
  removed: z.number().int(),
  reconciled: z.number().int(),
  errors: z.array(z.object({ step: z.string(), code: z.string(), message: z.string() })),
  notes: z.array(z.string()),
});
export type AdSyncReportDto = z.infer<typeof AdSyncReportDto>;

// ---------------------------------------------------------------------------
// Control
// ---------------------------------------------------------------------------

export const AdEntityPath = z.object({ level: AdLevel, id: Uuid });

export const AdStatusChange = z.object({ reason: z.string().trim().max(500).optional() });
export const AdBudgetChange = z.object({ amountMinor: MinorAmount, currency: Currency, reason: z.string().trim().max(500).optional() });
/** A null amount removes the cap where the platform allows it. */
export const AdCostCapChange = z.object({ amountMinor: MinorAmount.nullable(), currency: Currency, reason: z.string().trim().max(500).optional() });
export const AdRoleChange = z.object({ role: AdCampaignRole.nullable(), reason: z.string().trim().max(500).optional() });

export const AdActionDto = z.object({
  id: Uuid,
  connectionId: Uuid.nullable(),
  level: z.string(),
  entityId: Uuid.nullable(),
  externalId: z.string().nullable(),
  entityName: z.string().nullable(),
  action: AdActionKind,
  source: AdActionSource,
  status: AdActionStatus,
  raisesSpend: z.boolean(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  reason: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  actorKind: z.string(),
  actorName: z.string(),
  startedAt: IsoDateTime,
  finishedAt: IsoDateTime.nullable(),
  reconciledAt: IsoDateTime.nullable(),
});
export type AdActionDto = z.infer<typeof AdActionDto>;
export const AdActionPage = pageOf(AdActionDto);
export const AdActionListQuery = IdPageQuery.extend({
  connectionId: Uuid.optional(),
  entityId: Uuid.optional(),
  status: AdActionStatus.optional(),
  action: AdActionKind.optional(),
});

export const EmergencyStopInput = z.object({ reason: z.string().trim().max(500).optional() });
export const EmergencyReleaseInput = z.object({ reason: z.string().trim().min(1).max(500) });
export const EmergencyStopResultDto = z.object({
  stoppedAt: IsoDateTime,
  dryRun: z.boolean(),
  paused: z.array(z.object({ entityId: Uuid, name: z.string(), actionId: Uuid })),
  wouldPause: z.array(z.object({ entityId: Uuid, name: z.string(), actionId: Uuid })),
  failed: z.array(z.object({ entityId: Uuid, name: z.string(), actionId: Uuid, code: z.string() })),
  /** Accounts whose live list could not be read: a campaign the last sync did not see may still be running there. */
  unreadConnections: z.array(z.object({ connectionId: Uuid, name: z.string(), code: z.string() })),
  /** Accounts this service can no longer reach, disconnected or with no account, whose campaigns were last seen on. A person stops those on the platform. */
  unreachable: z.array(z.object({ connectionId: Uuid, platform: AdPlatform, name: z.string(), status: z.string(), campaigns: z.number().int() })),
});
export type EmergencyStopResultDto = z.infer<typeof EmergencyStopResultDto>;

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

export const AdGuardSettingsDto = z.object({
  capCurrency: z.string().nullable(),
  dailyCapMinor: z.number().int().nullable(),
  monthlyCapMinor: z.number().int().nullable(),
  /** The monthly budget of one channel, in `capCurrency`. Crossing it stops that channel only. */
  channelMonthlyCaps: z.partialRecord(AdPlatform, z.number().int()),
  /** What a lead and a customer should cost, in `capCurrency`. They report; they stop nothing. */
  targetCostPerLeadMinor: z.number().int().nullable(),
  targetCostPerClientMinor: z.number().int().nullable(),
  maxBudgetGrowthPct: z.number().int(),
  autoPauseEnabled: z.boolean(),
  emergencyStopAt: IsoDateTime.nullable(),
  emergencyStopBy: z.string().nullable(),
  emergencyStopReason: z.string().nullable(),
  lastGuardRunAt: IsoDateTime.nullable(),
  updatedAt: IsoDateTime.nullable(),
});
export type AdGuardSettingsDto = z.infer<typeof AdGuardSettingsDto>;

export const AdGuardSettingsPatch = z
  .object({
    capCurrency: Currency.optional(),
    dailyCapMinor: MinorAmount.nullable().optional(),
    monthlyCapMinor: MinorAmount.nullable().optional(),
    /** The whole list: a channel left out has no budget of its own. */
    channelMonthlyCaps: z.partialRecord(AdPlatform, MinorAmount).optional(),
    targetCostPerLeadMinor: MinorAmount.nullable().optional(),
    targetCostPerClientMinor: MinorAmount.nullable().optional(),
    maxBudgetGrowthPct: z.number().int().min(1).max(100).optional(),
    autoPauseEnabled: z.boolean().optional(),
  })
  .strict();

export const AdSpendConnectionDto = z.object({
  connectionId: Uuid,
  name: z.string(),
  platform: AdPlatform,
  currency: z.string().nullable(),
  /** The account's day and month the figures belong to. */
  day: IsoDay,
  todayMinor: z.number().int(),
  monthMinor: z.number().int(),
  spendSyncedAt: IsoDateTime.nullable(),
  unknownReason: SpendUnknownReason.nullable(),
});
export type AdSpendConnectionDto = z.infer<typeof AdSpendConnectionDto>;

/** One channel, spent against planned. `plannedMonthMinor` is what it has spent plus what its running budgets will spend by month end. */
export const AdChannelSpendDto = z.object({
  platform: AdPlatform,
  capMinor: z.number().int().nullable(),
  monthMinor: z.number().int(),
  activeDailyBudgetMinor: z.number().int(),
  plannedMonthMinor: z.number().int(),
  crossed: z.boolean(),
});
export type AdChannelSpendDto = z.infer<typeof AdChannelSpendDto>;

export const AdGuardStatusDto = z.object({
  settings: AdGuardSettingsDto,
  live: z.boolean(),
  /** The currency of the figures below. An account in another one is named in `connections`, never added in. */
  currency: z.string().nullable(),
  daysLeftInMonth: z.number().int(),
  /** Spent so far plus what running budgets will spend by month end. */
  plannedMonthMinor: z.number().int(),
  channels: z.array(AdChannelSpendDto),
  /** Known spend only, in the cap currency. With `spendUnknown` true these are a lower bound, never the truth. */
  todayMinor: z.number().int(),
  monthMinor: z.number().int(),
  activeDailyBudgetMinor: z.number().int(),
  spendUnknown: z.boolean(),
  dailyCapCrossed: z.boolean(),
  monthlyCapCrossed: z.boolean(),
  connections: z.array(AdSpendConnectionDto),
});
export type AdGuardStatusDto = z.infer<typeof AdGuardStatusDto>;

/** One guard run, asked for by a person. `skipped` means another run holds the lease and this one did nothing. */
export const AdGuardRunDto = z.object({
  skipped: z.boolean(),
  spendUnknown: z.boolean(),
  dailyCapCrossed: z.boolean(),
  monthlyCapCrossed: z.boolean(),
  /** Channels whose own monthly budget is crossed. Only their campaigns stop. */
  channelCapsCrossed: z.array(AdPlatform).optional(),
  paused: z.array(z.object({ entityId: Uuid, name: z.string(), actionId: Uuid })),
  wouldPause: z.array(z.object({ entityId: Uuid, name: z.string(), actionId: Uuid })),
  failed: z.array(z.object({ entityId: Uuid, name: z.string(), actionId: Uuid, code: z.string() })),
});
export type AdGuardRunDto = z.infer<typeof AdGuardRunDto>;

export const AdAlertDto = z.object({
  id: Uuid,
  connectionId: Uuid.nullable(),
  code: AdAlertCode,
  severity: AdAlertSeverity,
  details: z.unknown(),
  firstSeenAt: IsoDateTime,
  lastSeenAt: IsoDateTime,
  ackedAt: IsoDateTime.nullable(),
  ackedBy: z.string().nullable(),
  resolvedAt: IsoDateTime.nullable(),
});
export type AdAlertDto = z.infer<typeof AdAlertDto>;
export const AdAlertPage = pageOf(AdAlertDto);
export const AdAlertListQuery = IdPageQuery.extend({ open: QueryFlag.optional(), connectionId: Uuid.optional() });
